import {
  Button,
  Container,
  Grid,
  IconButton,
  Snackbar,
} from '@material-ui/core'
import {
  Apps,
  FormatListBulleted,
  ImportExport,
  Info,
  Search,
} from '@material-ui/icons'
import { Box } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { CSVLink } from 'react-csv'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { deleteProject, getAllProjects } from '../../../actions/projectActions'
import { listAdminUsers } from '../../../actions/userActions'
import filterIcon from '../../../assets/img/icons/filter.svg'
import { EMAIL_PROJECT_NOTIFICATION_RESET } from '../../../constants/emailConstants'
import { SIDE_NAV_CLOSE } from '../../../constants/navigationConstants'
import {
  PROJECT_CURRENT_STEP,
  PROJECT_DELETE_RESET,
  PROJECT_DETAILS_SUCCESS,
  PROJECT_UPDATE_QUOTE_RESET,
  PROJECT_UPDATE_RESET,
} from '../../../constants/projectConstants'
import { Role } from '../../../enum'
import { useDateFormatter } from '../../../hooks/useDateFormatter'
import { useInput } from '../../../hooks/useInput'
import { getAllProjectsExport } from '../../../modules/allProjectsExport'
import { fetchPromoCodes } from '../../../modules/promoCodes'
import { Context } from '../../../providers/ProjectContextProvider'
import ActionsMenu from '../../ActionsMenu/ActionsMenu'
import AutoCompleteSelect from '../../Form/AutoCompleteSelect'
import SelectInput from '../../Form/SelectInput'
import TextInput from '../../Form/TextInput'
import Loader from '../../Loader/Loader'
import Message from '../../Message/Message'
import ModalPopUp from '../../ModalPopUp/ModalPopUp'
import ToastMessage from '../../ToastMessage/ToastMessage'
import ArchiveModal from '../ProjectDetail/ArchiveModal'
import ProjectGrid from './ProjectGrid'
import ProjectTable from './ProjectTable'
import PromoCodeModal from './PromoCodeModal'

const headers = [
  { label: 'Project name', key: 'name' },
  { label: 'Status', key: 'status' },
  { label: 'Customer', key: 'userName' },
  { label: 'Company', key: 'company.companyName' },
  { label: 'Type', key: 'type' },
  { label: 'Creator', key: 'creator' },
  { label: 'Created At', key: 'createdAt' },
  { label: 'Cost', key: 'cost' },
]

const ProjectLibrary = ({ projectNotFound = false }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { setShowNotifyUser, selectedProjects, setShowPromoCodeModal } =
    useContext(Context)

  const { userInfo } = useSelector((state) => state.userLogin)
  const {
    loading,
    error,
    projects: userProjectList,
  } = useSelector((state) => state.projectList)
  const {
    loading: allProjectsLoading,
    error: allProjectsError,
    success: allProjectsSuccess,
    projects: allProjects,
  } = useSelector((state) => state.allProjectList)
  const { users: adminList } = useSelector((state) => state.userAdminList)
  const { success: successDelete } = useSelector((state) => state.projectDelete)
  const { success: successNotify, user: userNotified } = useSelector(
    (state) => state.emailProjectNotification
  )
  const { data: csvData, loading: csvLoading } = useSelector(
    (state) => state.allProjectsExport
  )

  const { bind: bindType, value: type, setValue: setType } = useInput('')
  const [search, setSearch] = useState(false)
  const { bind: bindStatus, value: status, setValue: setStatus } = useInput('')
  const { bind: bindClient, value: client, setValue: setClient } = useInput('')
  const {
    bind: bindCompany,
    value: company,
    setValue: setCompany,
  } = useInput('')
  const {
    bind: bindProjectName,
    value: projectName,
    setValue: setProjectName,
  } = useInput('')
  const { value: assignee, setValue: setAssignee } = useInput('')

  const [projectToDelete, setProjectToDelete] = useState({})
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDeleteToast, setShowDeleteToast] = useState(false)
  const [showNotifyToast, setShowNotifyToast] = useState(false)
  const [viewType, setViewType] = useState('table')
  const [showFilters, setShowFilters] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [filteredProjects, setFilteredProjects] = useState([])
  const [projects, setProjects] = useState([])
  const [sortValue, setSortValue] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(projectNotFound)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [formattedProjects, setFormattedProjects] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  useEffect(() => {
    // Run once on mount
    // Get all admin users if user is an admin
    if (userInfo.isAdmin) dispatch(listAdminUsers())
    // Get promo codes if user is a manager
    if (userInfo.role === Role.MANAGER) dispatch(fetchPromoCodes())
  }, [dispatch])

  useEffect(() => {
    if (successNotify) {
      setShowNotifyToast(true)
      setShowNotifyUser(false)
      dispatch({ type: EMAIL_PROJECT_NOTIFICATION_RESET })
    }

    if (userProjectList) {
      setFilteredProjects(userProjectList)
      setProjects(userProjectList)
    }

    if (allProjects) {
      setFilteredProjects(allProjects)
      setProjects(allProjects)
    }

    if (successDelete) {
      setShowDeleteModal(false)
      setShowDeleteToast(true)
      dispatch({ type: PROJECT_DELETE_RESET })
      dispatch({ type: SIDE_NAV_CLOSE })

      dispatch(getAllProjects())
    }

    dispatch({ type: PROJECT_UPDATE_RESET })
    dispatch({ type: PROJECT_UPDATE_QUOTE_RESET })
  }, [
    userInfo,
    dispatch,
    userProjectList,
    allProjects,
    successDelete,
    successNotify,
    setShowNotifyUser,
  ])

  useEffect(() => {
    if (userInfo.role !== 'Manager') return

    if (csvData) {
      setFormattedProjects(
        csvData.map((project) => ({
          ...project,
          createdAt: project.createdAt
            ? project.createdAt.substring(0, 10)
            : '',
          cost: project.charges
            .reduce((accumulator, object) => {
              return accumulator + object.price
            }, 0)
            .toFixed(2),
        }))
      )
    } else {
      dispatch(getAllProjectsExport())
    }
  }, [csvData])

  useEffect(() => {
    let sort

    switch (sortValue) {
      case 'newest':
        sort = { createdAt: -1 }
        break

      case 'oldest':
        sort = { createdAt: 1 }
        break

      case 'lastUpdated':
        sort = { updatedAt: -1 }
        break

      default:
        sort = { updatedAt: -1 }
        break
    }

    dispatch(
      getAllProjects(
        page,
        rowsPerPage,
        sort,
        status,
        assignee,
        projectName,
        client,
        company,
        type
      )
    )
  }, [
    assignee,
    dispatch,
    page,
    rowsPerPage,
    sortValue,
    status,
    search, // Run again on search button click
    type,
  ])

  const readableDate = useDateFormatter(new Date(), 'MM-DD-YYYY').replace(
    /[/]/g,
    '-'
  )

  const handleClearFilters = () => {
    setType('')
    setStatus('')
    setAssignee('')
  }

  const handleClearSearch = () => {
    setClient('')
    setCompany('')
    setProjectName('')
    setSearch(!search) // Toggle search to run useEffect and get fresh project list
  }

  const handleDeleteClick = (project) => {
    setProjectToDelete(project)
    setShowDeleteModal(true)
  }

  const handleArchiveClick = (project) => {
    dispatch({ type: PROJECT_DETAILS_SUCCESS, payload: project })
    setShowArchiveModal(true)
  }

  const handleEditViewClick = (project) => {
    if (project.status === 'New') {
      if (project.requests.length) {
        dispatch({
          type: PROJECT_CURRENT_STEP,
          payload: 5,
        })
      } else {
        dispatch({
          type: PROJECT_CURRENT_STEP,
          payload: 2,
        })
      }

      // Customers are sent to complete the project
      history.push(`/project/edit/${project._id}`)
    } else {
      history.push(`/myquality/project/${project._id}`)
    }
  }

  const confirmDeleteProject = () => {
    dispatch(deleteProject(projectToDelete._id))
  }

  const handleSnackbarClose = () => {
    setSnackbarOpen(false)
  }

  return (
    <div className="library gray-bg">
      <Container className="p-b-100">
        <h3 className="page-title accent-left">
          Project {userInfo.isAdmin ? 'list' : 'library'}
        </h3>
        <div className="flex space-between m-b-16" style={{ height: 33 }}>
          <Box>
            {userInfo.role === Role.MANAGER && (
              <Button
                variant="contained"
                color="primary"
                disabled={!selectedProjects.length}
                onClick={() => setShowPromoCodeModal(true)}
              >
                Send Promo Code
              </Button>
            )}
          </Box>

          <Box>
            <IconButton onClick={() => setShowSearch(!showSearch)}>
              <Search />
            </IconButton>

            <ActionsMenu
              icon={<ImportExport />}
              options={[
                { onClick: () => setSortValue('newest'), label: 'Newest' },
                { onClick: () => setSortValue('oldest'), label: 'Oldest' },
                {
                  onClick: () => setSortValue('lastUpdated'),
                  label: 'Last updated',
                },
              ]}
            />

            <IconButton
              className="m-l-5"
              style={{ width: 32, height: 33 }}
              onClick={() => setShowFilters(!showFilters)}
            >
              <img style={{ width: 20 }} src={filterIcon} alt="Filter" />
            </IconButton>

            {viewType === 'table' ? (
              <IconButton className="m-l-5" onClick={() => setViewType('grid')}>
                <Apps />
              </IconButton>
            ) : (
              <IconButton
                className="m-l-5"
                onClick={() => setViewType('table')}
              >
                <FormatListBulleted />
              </IconButton>
            )}

            {userInfo.role === 'Manager' && projects && projects.length > 0 && (
              <CSVLink
                data={formattedProjects}
                headers={headers}
                filename={`MyQuality Projects - ${readableDate}.csv`}
                className="hide-580"
                disabled={csvLoading}
              >
                <Button
                  className="m-l-24 hide-580"
                  variant="outlined"
                  color="primary"
                >
                  Export projects
                </Button>
              </CSVLink>
            )}

            <Button
              className="m-l-24"
              variant="contained"
              color="primary"
              onClick={() => history.push('/createaquote')}
            >
              New project
            </Button>
          </Box>
        </div>

        {showSearch && (
          <div className="filter-bar">
            <div className="flex space-between">
              <label className="fs14 strong">Search</label>
              <span
                className="link fs12"
                rel="noopener noreferrer"
                target="_blank"
                onClick={handleClearSearch}
              >
                Clear
              </span>
            </div>
            <div className="form-container p-l-0 p-r-0">
              {userInfo.isAdmin ? (
                <Grid container spacing={2}>
                  <Grid item sm={3} xs={6}>
                    <TextInput
                      name="projectName"
                      label="Project name"
                      bindValue={bindProjectName}
                    />
                  </Grid>
                  <Grid item sm={3} xs={6}>
                    <TextInput
                      name="client"
                      label="Client"
                      bindValue={bindClient}
                    />
                  </Grid>
                  <Grid item sm={3} xs={6}>
                    <TextInput
                      name="company"
                      label="Company"
                      bindValue={bindCompany}
                    />
                  </Grid>

                  <Grid
                    item
                    sm={3}
                    xs={6}
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'flex-end',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setSearch(!search)}
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item sm={3} xs={6}>
                    <TextInput
                      name="projectName"
                      label="Project name"
                      bindValue={bindProjectName}
                    />
                  </Grid>
                  <Grid
                    item
                    sm={3}
                    xs={6}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      marginBottom: '3px',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setSearch(!search)}
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              )}
            </div>
          </div>
        )}

        {showFilters && (
          <div className="filter-bar">
            <div className="flex space-between">
              <label className="fs14 strong">Filters</label>
              <span
                className="link fs12"
                rel="noopener noreferrer"
                target="_blank"
                onClick={handleClearFilters}
              >
                Clear
              </span>
            </div>
            <div className="form-container p-l-0 p-r-0">
              {userInfo.isAdmin ? (
                <Grid container spacing={2}>
                  <Grid item sm={3} xs={6}>
                    <TextInput
                      name="company"
                      label="Company"
                      bindValue={bindCompany}
                    />
                  </Grid>
                  <Grid item sm={3} xs={6}>
                    <SelectInput
                      name="status"
                      label="Status"
                      options={[
                        { value: 'New', label: 'New' },
                        {
                          value: 'Submitted for review',
                          label: 'Submitted for review',
                        },
                        { value: 'Quote submitted', label: 'Quote submitted' },
                        { value: 'In progress', label: 'In progress' },
                        {
                          value: 'Pending acceptance',
                          label: 'Pending acceptance',
                        },
                        { value: 'Complete', label: 'Complete' },
                      ]}
                      bindValue={bindStatus}
                    />
                  </Grid>

                  {adminList && (
                    <Grid item sm={3} xs={6}>
                      <AutoCompleteSelect
                        name="assignee"
                        label="Assignee"
                        options={adminList.map((x) => ({
                          value: x._id,
                          label: x.firstName + ' ' + x.lastName,
                        }))}
                        value={assignee}
                        setValue={setAssignee}
                      />
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item sm={3} xs={6}>
                    <SelectInput
                      name="type"
                      label="Type"
                      options={[
                        { value: 'programming', label: 'Programming' },
                        { value: 'inspection', label: 'Inspection' },
                      ]}
                      bindValue={bindType}
                    />
                  </Grid>
                </Grid>
              )}
            </div>
          </div>
        )}
        {(loading || allProjectsLoading) && <Loader />}
        {error && <Message variant="error">{error}</Message>}
        {allProjectsError && (
          <Message variant="error">{allProjectsError}</Message>
        )}

        {allProjectsSuccess && projects && projects.length === 0 && (
          <div className="flex flex-column align-center m-t-150">
            <Info
              className="m-b-35"
              style={{ fontSize: 42, color: '#cacaca' }}
            />
            <h5 className="gray-text fs18 m-b-32">No projects.</h5>
          </div>
        )}

        {!loading && !allProjectsLoading && filteredProjects.length > 0 && (
          <>
            {viewType === 'table' && (
              <ProjectTable
                projects={filteredProjects}
                handleDeleteClick={handleDeleteClick}
                handleArchiveClick={handleArchiveClick}
                handleEditViewClick={handleEditViewClick}
                setRowsPerPage={setRowsPerPage}
                rowsPerPage={rowsPerPage}
                page={page}
                setPage={setPage}
              />
            )}
            {viewType === 'grid' && (
              <ProjectGrid
                projects={filteredProjects}
                handleDeleteClick={handleDeleteClick}
                handleArchiveClick={handleArchiveClick}
                handleEditViewClick={handleEditViewClick}
              />
            )}
          </>
        )}
      </Container>

      <ModalPopUp
        title="Delete project"
        setShowModal={setShowDeleteModal}
        showModal={showDeleteModal}
      >
        <h4 className="m-b-32">Delete project?</h4>
        <p>
          Are you sure you want to delete {projectToDelete.name}? By deleting
          this project, you will lose all previous activity communications about
          the project.
        </p>
        <div className="actions m-t-40 flex-end flex">
          <Button
            color="primary"
            variant="outlined"
            className="m-r-24"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={confirmDeleteProject}
          >
            Confirm
          </Button>
        </div>
      </ModalPopUp>

      <ArchiveModal
        showArchiveModal={showArchiveModal}
        setShowArchiveModal={setShowArchiveModal}
      />

      <PromoCodeModal />

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Project deleted</p>
            <p className="fs14 m-b-0">
              You successfully deleted {projectToDelete.name}.
            </p>
          </>
        }
        open={showDeleteToast}
        setOpen={setShowDeleteToast}
      />

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Email sent</p>
            <p className="fs14 m-b-0">
              {userNotified && userNotified.firstName}{' '}
              {userNotified && userNotified.lastName} has been notified about{' '}
              <strong>{userNotified && userNotified.project}</strong>.
            </p>
          </>
        }
        open={showNotifyToast}
        setOpen={setShowNotifyToast}
      />

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message="Project does not exist."
        key={'top center'}
        className="warning"
      />
    </div>
  )
}

export default ProjectLibrary
